import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Label } from './ui/label';
import cidadesBrasil from '../data/cidades-brasil.json';

const ESTADOS = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' },
];

export const StateCitySelect = ({
  estado,
  cidade,
  onEstadoChange,
  onCidadeChange,
  required = false,
  showLabels = true,
  estadoTestId = 'select-estado',
  cidadeTestId = 'select-cidade',
  allowedStates = null, // optional array of UF codes to restrict the list
}) => {
  const [openEstado, setOpenEstado] = useState(false);
  const [openCidade, setOpenCidade] = useState(false);

  const estadosVisiveis = useMemo(() => {
    if (!allowedStates || !allowedStates.length) return ESTADOS;
    const set = new Set(allowedStates);
    const filtered = ESTADOS.filter((e) => set.has(e.sigla));
    return filtered.length ? filtered : ESTADOS;
  }, [allowedStates]);

  const cidades = useMemo(() => {
    if (!estado || !cidadesBrasil[estado]) return [];
    return cidadesBrasil[estado];
  }, [estado]);

  const estadoSelecionado = estadosVisiveis.find((e) => e.sigla === estado);

  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="space-y-2">
        {showLabels && <Label htmlFor="estado">Estado{required && ' *'}</Label>}
        <Popover open={openEstado} onOpenChange={setOpenEstado}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={openEstado}
              className="w-full justify-between font-normal"
              data-testid={estadoTestId}
            >
              {estadoSelecionado ? estadoSelecionado.sigla : 'UF'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0 z-[100]" align="start">
            <Command>
              <CommandInput placeholder="Buscar estado..." />
              <CommandList>
                <CommandEmpty>Nenhum estado encontrado.</CommandEmpty>
                <CommandGroup>
                  {estadosVisiveis.map((e) => (
                    <CommandItem
                      key={e.sigla}
                      value={`${e.sigla} ${e.nome}`}
                      onSelect={() => {
                        onEstadoChange(e.sigla);
                        setOpenEstado(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          estado === e.sigla ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      <span className="font-bold mr-2">{e.sigla}</span>
                      <span className="text-muted-foreground">{e.nome}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <div className="col-span-2 space-y-2">
        {showLabels && <Label htmlFor="cidade">Cidade{required && ' *'}</Label>}
        <Popover open={openCidade} onOpenChange={setOpenCidade}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={openCidade}
              className="w-full justify-between font-normal"
              disabled={!estado}
              data-testid={cidadeTestId}
            >
              {cidade || (estado ? 'Selecione a cidade...' : 'Escolha o estado primeiro')}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] p-0 z-[100]" align="start">
            <Command>
              <CommandInput placeholder="Buscar cidade..." />
              <CommandList>
                <CommandEmpty>Nenhuma cidade encontrada.</CommandEmpty>
                <CommandGroup>
                  {cidades.map((c) => (
                    <CommandItem
                      key={c}
                      value={c}
                      onSelect={() => {
                        onCidadeChange(c);
                        setOpenCidade(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          cidade === c ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                      {c}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export { ESTADOS };
